import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';
import io from 'socket.io-client';
import { socketOrigin } from '../services/socketOrigin';
import { AlertTriangle, Car, ChevronUp, ChevronDown, FileText, LocateFixed, MoreHorizontal, Phone, Search } from 'lucide-react';
import './LiveMap.css';
import { carMarkerSvg, isCompactCarSize, carSizeForZoom } from '../lib/carMarker';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Betriebsregion (Duisburg/Ruhrgebiet, = App-Default): die Karte startet IMMER hier —
// NICHT ganz Deutschland, NICHT ganz NRW. Mittlerer „große Mitte"-Zoom (Stadtregion).
const OPERATING_CENTER = [51.4344, 6.7623];
const OPERATING_ZOOM = 11;

// LIVEKARTE-UBER-001: helle Karte (Vorbild Flottenportal des Marktführers) — gleich wie die Admin-Karte.
const MAP_STYLE = 'navigation-day-v1';

// Farbvertrag (Owner 19.09.2026, ersetzt den vom 04.09.): die Farbe sagt, WOHIN es geht —
//   blau = zum Abholort (Auto „Zum Abholort" + Linie zum Abholort) ·
//   grün = zum Ziel (Auto „Mit Fahrgast" + Linie zum Ziel) · orange = frei · grau = offline.
// Ein Auto trägt die Farbe seiner Linie. Dieselben Hexwerte in der Admin-Karte (admin-app/src/pages/LiveMap.js),
// in Legende, Zählern, Liste, Popups und Folgefahrt-Hinweisen.
// Rot-Grün-Schwäche: Grün ist dunkel, Orange hell (Leuchtdichte 1,8:1), Blau/Grün trennt der Farbton
// (die Blau-Gelb-Achse bleibt erhalten) und zusätzlich das Linienmuster.
export const PHASE_COLOR = { free: '#6b7280', pickup: '#2563eb', pax: '#15803d' };   // frei grau (Owner 08.10.2026, vorher orange)
// Schrift in Phasenfarbe (≥ 4,5:1 auf Weiß, design-system/MASTER.md): Orange ist als Schrift zu hell.
export const PHASE_TEXT = { free: '#374151', pickup: '#2563eb', pax: '#15803d' };
export const PHASE_LABEL = { free: 'Frei', pickup: 'Zum Abholort', pax: 'Mit Fahrgast' };
export const OFFLINE_COLOR = '#9ca3af';   // nur Dekor (Punkt), nie Schrift
export const OFFLINE_TEXT = '#4b5563';    // Muted (Lesbarkeits-Vertrag)
// Auswahl = Ink-Ring (MASTER.md: Ink = aktive Zustände), nie Blau — Blau heißt „zum Abholort".
const SELECT_RING = '#0a0a0a';
// Linien: Farbe = wohin (blau Abholort, grün Ziel), Muster = Stand. Gestrichelt = Anfahrt zum Abholort,
// durchgezogen = mit Fahrgast zum Ziel, gepunktet + heller = geplanter Ziel-Abschnitt (Angekommen, vor dem
// Abholen). Auswahl: dicker + deckender, Farbe bleibt.
export const LINE_STYLE = {
  pickup: { color: PHASE_COLOR.pickup, dashArray: '8 6', opacity: 0.9, label: 'Zum Abholort' },
  dropoff: { color: PHASE_COLOR.pax, dashArray: null, opacity: 0.9, label: 'Zum Ziel · mit Fahrgast' },
  planned: { color: PHASE_COLOR.pax, dashArray: '1 8', opacity: 0.7, label: 'Zum Ziel · geplant' },
};
export const linePathOptions = (kind, selected = false) => {
  const style = LINE_STYLE[kind] || LINE_STYLE.planned;
  return {
    color: style.color,
    weight: selected ? 6 : 4,
    opacity: selected ? Math.min(1, style.opacity + 0.15) : style.opacity,
    dashArray: style.dashArray,
    lineCap: 'round',
    lineJoin: 'round',
  };
};

export const driverPhase = (d) => (d.is_online && d.ride_status === 'in_progress') ? 'pax'
  : (d.is_online && (d.ride_status === 'accepted' || d.ride_status === 'arrived')) ? 'pickup' : 'free';
export const driverColor = (d) => !d.is_online ? OFFLINE_COLOR : PHASE_COLOR[driverPhase(d)];
export const driverTextColor = (d) => !d.is_online ? OFFLINE_TEXT : PHASE_TEXT[driverPhase(d)];
export const driverLabel = (d) => !d.is_online ? 'Offline' : PHASE_LABEL[driverPhase(d)];

// Legenden-Muster: dieselbe Linie wie auf der Karte, auf einem kartenhellen Plättchen (so wirkt „heller" wie dort).
function LineSample({ kind }) {
  const style = LINE_STYLE[kind];
  return (
    <svg className="pp-map-legend-line" width="28" height="12" viewBox="0 0 28 12" aria-hidden="true" data-line-kind={kind}>
      <rect width="28" height="12" rx="3" fill="#f2f3f0" />
      <line x1="4" y1="6" x2="24" y2="6" stroke={style.color} strokeWidth="4" strokeLinecap="round"
        strokeOpacity={style.opacity} strokeDasharray={style.dashArray || undefined} />
    </svg>
  );
}

// Karten-Auto „V4-kräftig" (Owner-Freigabe 08.10.2026): weißes Top-Down-Auto aus src/lib/carMarker.js, gedreht in
// Fahrtrichtung, Statusfarbe als Schein darunter. Ausgewählter Fahrer bekommt zusätzlich den Ink-Auswahlring
// (SELECT_RING) — der Statusschein behält seine Phasenfarbe. data-phase-color trägt die Phasenfarbe maschinenlesbar.
const carIcon = (phase, size, bearing = 0, selected = false) => {
  const color = PHASE_COLOR[phase] || PHASE_COLOR.free;
  const ring = selected
    ? `<div class="pp-car-select-ring" style="position:absolute;inset:0;border-radius:50%;border:3px solid ${SELECT_RING};box-shadow:0 0 0 6px ${SELECT_RING}2e;"></div>`
    : '';
  return new L.DivIcon({
    html: `<div class="pp-map-car-body" data-phase="${phase}" data-phase-color="${color}" style="position:relative;width:${size}px;height:${size}px;">${ring}
      <div style="position:absolute;inset:0;transform:rotate(${bearing}deg);transform-origin:center;">${carMarkerSvg(color, isCompactCarSize(size))}</div>
    </div>`,
    iconSize: [size, size], iconAnchor: [Math.round(size / 2), Math.round(size / 2)], className: 'pp-map-car',
  });
};
// Größe je Zoom: 24 px auf der Regionsansicht … 48 px in der Straßenansicht (src/lib/carMarker.js).
export { carSizeForZoom };

// Abholort = blauer Punkt (Farbe der Linie zum Abholort) · Ziel = schwarzes Quadrat (gleich wie die Admin-Karte).
// Bewusst LAZY (erst beim ersten Rendern gebaut): das Modul wird von App.js beim Start geladen —
// ein Konstruktor-Aufruf beim Import würde jede Seite mitziehen, die die Karte gar nicht zeigt.
const lazyIcon = (html) => { let icon = null; return () => icon || (icon = new L.DivIcon({ html, iconSize: [12, 12], iconAnchor: [6, 6], className: '' })); };
const pickupIcon = lazyIcon(`<div style="width:12px;height:12px;background:${PHASE_COLOR.pickup};border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 1px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.25)"></div>`);
const dropoffIcon = lazyIcon('<div style="width:12px;height:12px;background:#0a0a0a;border:3px solid #fff;border-radius:2px;box-shadow:0 0 0 1px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.25)"></div>');

// Zoom-Beobachter: aktualisiert die Markergröße bei Zoom-Änderung.
function ZoomWatcher({ onZoom }) {
  const map = useMapEvents({ zoomend: () => onZoom(map.getZoom()) });
  return null;
}

// Fliegt zum ausgewählten Fahrer (Klick in der Liste) — nie automatisch, nie beim Nachladen.
function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target || !map || typeof map.flyTo !== 'function') return;
    const zoom = typeof map.getZoom === 'function' ? Math.max(map.getZoom(), 14) : 14;
    map.flyTo([target.lat, target.lng], zoom, { duration: 0.8 });
  }, [target, map]);
  return null;
}

// Gleitendes Auto: die Position wandert in ~0,9 s zur neuen Koordinate statt zu springen.
// react-leaflet setzt `position` erst am Ende (dann steht das Auto schon dort); dazwischen führt
// dieser Baustein den Leaflet-Marker selbst. Ohne echtes Leaflet (Tests) springt es direkt.
export const GLIDE_MS = 900;
export function GlidingMarker({ position, icon, eventHandlers, children }) {
  const markerRef = useRef(null);
  const rafRef = useRef(null);
  const [shown, setShown] = useState(position);
  useEffect(() => {
    const marker = markerRef.current;
    const same = shown && Math.abs(shown.lat - position.lat) < 1e-9 && Math.abs(shown.lng - position.lng) < 1e-9;
    if (same) return undefined;
    if (!marker || typeof marker.setLatLng !== 'function' || typeof marker.getLatLng !== 'function'
      || typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setShown(position);
      return undefined;
    }
    const from = marker.getLatLng();
    const start = performance.now();
    const step = (now) => {
      const k = Math.min(1, (now - start) / GLIDE_MS);
      const e = 1 - Math.pow(1 - k, 3);   // ease-out
      marker.setLatLng([from.lat + (position.lat - from.lat) * e, from.lng + (position.lng - from.lng) * e]);
      if (k < 1) rafRef.current = window.requestAnimationFrame(step);
      else { rafRef.current = null; setShown(position); }
    };
    if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = window.requestAnimationFrame(step);
    return () => { if (rafRef.current) { window.cancelAnimationFrame(rafRef.current); rafRef.current = null; } };
  }, [position.lat, position.lng]);
  return (
    <Marker ref={markerRef} position={[shown.lat, shown.lng]} icon={icon} eventHandlers={eventHandlers}>
      {children}
    </Marker>
  );
}

// Linie wie beim Marktführer: zum Kunden → Auto→Abholort · mit Fahrgast → Auto→Ziel (durchgezogen).
// „Angekommen" steht am Abholort: Ziel-Marke + geplanter Abschnitt zum Ziel.
// `kind` ist die Linienart des Farbvertrags (LINE_STYLE, Owner 19.09.2026):
//   'pickup'  = zum Abholort (blau) · 'dropoff' = zum Ziel mit Fahrgast (grün) ·
//   'planned' = zum Ziel, noch nicht gefahren (grün, gepunktet + heller).
export function rideGeometry(ride, pos, phase) {
  if (!ride || !pos) return null;
  const hasPickup = Number.isFinite(ride.pickup_lat) && Number.isFinite(ride.pickup_lng);
  const hasDropoff = Number.isFinite(ride.dropoff_lat) && Number.isFinite(ride.dropoff_lng);
  const car = [pos.lat, pos.lng];
  if (phase === 'pickup' && ride.status === 'accepted' && hasPickup) {
    return { line: [car, [ride.pickup_lat, ride.pickup_lng]], kind: 'pickup', dashed: true, pickup: hasPickup, dropoff: hasDropoff };
  }
  if (hasDropoff) {
    const unterwegs = ride.status === 'in_progress';
    return { line: [car, [ride.dropoff_lat, ride.dropoff_lng]], kind: unterwegs ? 'dropoff' : 'planned', dashed: !unterwegs, pickup: !unterwegs && hasPickup, dropoff: true };
  }
  return { line: null, kind: null, dashed: false, pickup: hasPickup, dropoff: false };
}

const vehicleLine = (d) => [d.license_plate, [d.vehicle_year, d.vehicle_brand, d.vehicle_model].filter(Boolean).join(' ')]
  .filter((x) => x && String(x).trim()).join(' · ');

function Avatar({ d, size = 40 }) {
  const [broken, setBroken] = useState(false);
  const initial = (d.name || '?').trim().charAt(0).toUpperCase();
  return (
    <span className="pp-map-avatar" style={{ width: size, height: size }}>
      {d.avatar && !broken
        ? <img src={d.avatar} alt="" onError={() => setBroken(true)} />
        : <span className="pp-map-avatar-initial">{initial}</span>}
      <i className="pp-map-avatar-dot" style={{ background: driverColor(d) }} aria-label={driverLabel(d)} />
    </span>
  );
}

// ── BETRIEB-SENTRY-KLEINFUNDE-001 · Teil A ───────────────────────────────────
// Sentry 140571743 (13.08.2026, 22:59:58 Berlin, EIN Vorfall): „AxiosError: Network Error",
// culprit `/live-map`, Projekt `voogo-web`. Der Ursprung war genau die Stelle unten:
// der Lade-Aufruf lief OHNE `.catch`. Bei einem Netzausfall blieb die Ablehnung unbehandelt →
// `window.onunhandledrejection` → Sentry; der Nutzer sah dagegen GAR NICHTS: keine Meldung, kein
// Weg zurück, und alle 15 Sekunden lief derselbe Fehlversuch stumm weiter — unbegrenzt.
//
// Regeln dieser Reparatur:
//  • Netzfehler → verständliche Meldung + „Erneut versuchen"; die zuletzt bestätigten
//    Positionen bleiben stehen (stale-but-honest).
//  • Begrenzt: nach MAX_FEHLVERSUCHE aufeinanderfolgenden Fehlschlägen hält das Nachladen
//    an. Ein kurzer Ausfall darf keine Dauerlast auf dem eigenen Server werden.
//  • Eine abgelaufene oder nicht ausreichende Anmeldung (401/403) ist KEIN Netzfehler:
//    sie führt zur Anmeldung und nie in die Wiederholschleife.
//  • In der Fehleranzeige stehen keine Fahrer- oder Kundendaten.
//
// LIVEKARTE-UBER-001: Datenquelle ist jetzt GET /partners/live-map (Fahrer MIT Foto + Kurs UND die
// aktiven Fahrten der eigenen Flotte). Live-Positionen kommen über `live_map_position` in den
// Partnerraum (join_partner) — der Poll bleibt als Rückfallebene.
const NACHLADE_INTERVALL_MS = 15000;
const MAX_FEHLVERSUCHE = 5;
export const LIVE_EVENT = 'live_map_position';

export default function LiveMap() {
  const [drivers, setDrivers] = useState([]);
  const [rides, setRides] = useState([]);
  const [positions, setPositions] = useState({});   // driver_id → { lat, lng, heading }
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('online'); // online (Start) | all | offline | free | pickup | pax
  const [zoom, setZoom] = useState(OPERATING_ZOOM);   // Kartenzoom → Markergröße (Start: Betriebsregion)
  const [selectedId, setSelectedId] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const [listOpen, setListOpen] = useState(true);
  const [fehler, setFehler] = useState(null);         // { art: 'netz' | 'sitzung', text }
  const [angehalten, setAngehalten] = useState(false);  // true → kein Timer, kein Socket
  const socketRef = useRef(null);
  const fehlversucheRef = useRef(0);   // aufeinanderfolgende Fehlschläge (Erfolg setzt zurück)

  // Jeder Ausgang ist behandelt — diese Kette darf NIE unbehandelt abgelehnt werden.
  const load = useCallback(() => api.get('/partners/live-map').then(r => {
    const list = r.data.drivers || [];
    setDrivers(list);
    setRides(r.data.active_rides || []);
    setPositions(prev => {
      const pos = {};
      list.forEach(d => {
        if (d.current_lat != null && d.current_lng != null) {
          const p = prev[d.id];
          // Eine frischere Socket-Position gewinnt gegen den Poll-Stand nicht um Meter zurück:
          // der Poll setzt nur, wenn er weiter als ~1 m abweicht (Server ist die Wahrheit).
          pos[d.id] = p && Math.abs(p.lat - d.current_lat) < 1e-5 && Math.abs(p.lng - d.current_lng) < 1e-5
            ? p : { lat: d.current_lat, lng: d.current_lng, heading: Number(d.current_heading) || (p ? p.heading : 0) };
        }
      });
      return pos;
    });
    fehlversucheRef.current = 0;
    setFehler(null);
  }).catch(err => {
    const status = err && err.response && err.response.status;
    // Anmeldung abgelaufen (401) oder Zugang reicht nicht (403): kein Netzfehler, keine
    // Wiederholung — der Weg führt zur Anmeldung. (Den 401 leitet der Interceptor selbst um.)
    if (status === 401 || status === 403) {
      fehlversucheRef.current = 0;
      setFehler({
        art: 'sitzung',
        text: status === 401
          ? 'Deine Anmeldung ist abgelaufen. Bitte melde dich neu an.'
          : 'Dein Zugang reicht für die Live-Karte nicht aus. Bitte melde dich neu an.',
      });
      setAngehalten(true);
      return;
    }
    fehlversucheRef.current += 1;
    const erschoepft = fehlversucheRef.current >= MAX_FEHLVERSUCHE;
    setFehler({
      art: 'netz',
      text: erschoepft
        ? `Die Live-Karte konnte ${MAX_FEHLVERSUCHE}-mal in Folge nicht geladen werden. Das automatische Nachladen ist angehalten.`
        : 'Die Live-Karte konnte gerade nicht geladen werden — die Verbindung wurde unterbrochen.',
    });
    if (erschoepft) setAngehalten(true);
  }), []);

  // Angehalten heißt: kein Timer, kein Socket, kein Aufruf. Erst der Nutzer startet wieder.
  useEffect(() => {
    if (angehalten) return undefined;
    load();
    const socket = io(socketOrigin(), { auth: { token: localStorage.getItem('partner_token') }, extraHeaders: { 'ngrok-skip-browser-warning': 'true' } });
    socketRef.current = socket;
    // Der Partnerraum wird serverseitig aus der Token-Identität abgeleitet — nur die eigene Flotte.
    const join = () => { if (typeof socket.emit === 'function') socket.emit('join_partner'); };
    socket.on('connect', join);
    join();
    socket.on(LIVE_EVENT, d => {
      if (!d || !d.driver_id || !Number.isFinite(d.lat) || !Number.isFinite(d.lng)) return;
      setPositions(p => ({ ...p, [d.driver_id]: { lat: d.lat, lng: d.lng, heading: Number.isFinite(d.heading) ? d.heading : (p[d.driver_id] ? p[d.driver_id].heading : 0) } }));
    });
    socket.on('driver_status_changed', load);
    const iv = setInterval(load, NACHLADE_INTERVALL_MS);
    return () => { socket.disconnect(); clearInterval(iv); };
  }, [load, angehalten]);

  // „Erneut versuchen" ist der einzige Weg aus dem angehaltenen Zustand — nie ein Timer.
  const erneutVersuchen = useCallback(() => {
    fehlversucheRef.current = 0;
    setFehler(null);
    if (angehalten) setAngehalten(false);   // Effekt startet Timer + Socket neu
    else load();
  }, [angehalten, load]);

  const rideByDriver = useMemo(() => {
    const byId = Object.fromEntries(rides.map(ride => [ride.id, ride]));
    return Object.fromEntries(drivers
      .filter(driver => driver.active_ride_id && byId[driver.active_ride_id])
      .map(driver => [driver.id, byId[driver.active_ride_id]]));
  }, [drivers, rides]);
  const followupsByDriver = useMemo(() => {
    const driverById = Object.fromEntries(drivers.map(driver => [driver.id, driver]));
    return rides.reduce((result, ride) => {
      const driver = driverById[ride.driver_id];
      if (!driver || ride.status !== 'accepted' || ride.id === driver.active_ride_id) return result;
      if (!result[ride.driver_id]) result[ride.driver_id] = [];
      result[ride.driver_id].push(ride);
      return result;
    }, {});
  }, [drivers, rides]);

  const onlineDrivers = drivers.filter(d => d.is_online);
  const pickupCount = onlineDrivers.filter(d => driverPhase(d) === 'pickup').length;
  const paxCount = onlineDrivers.filter(d => driverPhase(d) === 'pax').length;

  const filtered = drivers.filter(d => {
    if (statusFilter === 'online' && !d.is_online) return false;
    if (statusFilter === 'offline' && d.is_online) return false;
    if ((statusFilter === 'free' || statusFilter === 'pickup' || statusFilter === 'pax') && (!d.is_online || driverPhase(d) !== statusFilter)) return false;
    return (d.name || '').toLowerCase().includes(search.toLowerCase());
  });
  const carSize = carSizeForZoom(zoom);

  const selectDriver = (d) => {
    setMenuFor(null);
    const next = selectedId === d.id ? null : d.id;
    setSelectedId(next);
    const pos = positions[d.id];
    if (next && pos) setFlyTarget({ lat: pos.lat, lng: pos.lng, at: Date.now() });
  };
  const showOnMap = (d) => {
    setMenuFor(null);
    setSelectedId(d.id);
    const pos = positions[d.id];
    if (pos) setFlyTarget({ lat: pos.lat, lng: pos.lng, at: Date.now() });
  };

  return (
    <div className="pp-live-map" style={{ position: 'relative', height: 'calc(100vh - 61px)', width: '100%' }}>
      {/* Karte startet IMMER auf der Betriebsregion (kein Auto-Fit auf alle Fahrer = ganz NRW). */}
      <MapContainer center={OPERATING_CENTER} zoom={OPERATING_ZOOM} style={{ height: '100%', width: '100%' }} zoomControl={false}>
        <TileLayer url={`https://api.mapbox.com/styles/v1/mapbox/${MAP_STYLE}/tiles/256/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`} attribution='&copy; Mapbox &copy; OpenStreetMap' tileSize={256} />
        <ZoomWatcher onZoom={setZoom} />
        <FlyTo target={flyTarget} />

        {/* Linien + Marken der aktiven Fahrten (eigene Flotte) — nur für Fahrer, die online auf der Karte stehen. */}
        {drivers.map(d => {
          const pos = positions[d.id];
          const ride = rideByDriver[d.id];
          if (!pos || !d.is_online || !ride) return null;
          const geo = rideGeometry(ride, pos, driverPhase(d));
          if (!geo) return null;
          const selected = selectedId === d.id;
          return (
            <React.Fragment key={`ride-${ride.id}`}>
              {geo.line && (
                <Polyline positions={geo.line} pathOptions={linePathOptions(geo.kind, selected)} />
              )}
              {geo.pickup && (
                <Marker position={[ride.pickup_lat, ride.pickup_lng]} icon={pickupIcon()}>
                  <Popup><div className="pp-map-popup"><strong>Abholort</strong><span>{ride.pickup_address || 'Adresse nicht verfügbar'}</span></div></Popup>
                </Marker>
              )}
              {geo.dropoff && (
                <Marker position={[ride.dropoff_lat, ride.dropoff_lng]} icon={dropoffIcon()}>
                  <Popup><div className="pp-map-popup"><strong>Ziel</strong><span>{ride.dropoff_address || 'Adresse nicht verfügbar'}</span></div></Popup>
                </Marker>
              )}
            </React.Fragment>
          );
        })}

        {/* Autos: nur Online-Fahrer — eine alte Offline-Position wäre eine falsche Aussage. */}
        {drivers.map(d => {
          const pos = positions[d.id];
          if (!pos || !d.is_online) return null;
          const selected = selectedId === d.id;
          return (
            <GlidingMarker key={d.id} position={pos} icon={carIcon(driverPhase(d), carSize, pos.heading || 0, selected)}
              eventHandlers={{ click: () => setSelectedId(d.id) }}>
              <Popup>
                <div className="pp-map-popup">
                  <strong>{d.name}</strong>
                  <span>{vehicleLine(d) || 'Kein Fahrzeug hinterlegt'}</span>
                  <span style={{ color: driverTextColor(d), fontWeight: 700 }}>{driverLabel(d)}</span>
                </div>
              </Popup>
            </GlidingMarker>
          );
        })}
      </MapContainer>

      {/* Fehlerband: nur Klartext über den Zustand der Karte — bewusst OHNE Fahrer-/Kundendaten. */}
      {fehler && (
        <div
          role="status"
          data-testid="live-map-fehler"
          style={{
            position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1100, maxWidth: 520, display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 14px', borderRadius: 12, background: '#fff',
            border: '1px solid #fecaca', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            fontSize: 13, color: '#0a0a0a',
          }}
        >
          <AlertTriangle size={16} color="#dc2626" style={{ flexShrink: 0 }} />
          <span>
            {fehler.text}
            {fehler.art === 'netz' && drivers.length > 0 && ' Die zuletzt bestätigten Positionen bleiben sichtbar.'}
          </span>
          {fehler.art === 'sitzung' ? (
            <a
              href="/login"
              style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 8, background: '#0a0a0a', color: '#fff', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}
            >
              Zur Anmeldung
            </a>
          ) : (
            <button
              type="button"
              onClick={erneutVersuchen}
              style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 8, border: 'none', background: '#0a0a0a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >
              Erneut versuchen
            </button>
          )}
        </div>
      )}

      {/* Legende: Farbvertrag der Karte — gleich wie im Admin. */}
      <div className="pp-map-legend" data-testid="live-map-legend">
        <strong>Fahrerstatus</strong>
        <span><i style={{ background: PHASE_COLOR.free }} /> Frei (online)</span>
        <span><i style={{ background: PHASE_COLOR.pickup }} /> Zum Abholort</span>
        <span><i style={{ background: PHASE_COLOR.pax }} /> Mit Fahrgast</span>
        <span><i style={{ background: OFFLINE_COLOR }} /> Offline · nicht auf der Karte</span>
        <span><i className="is-pickup" /> Abholort</span>
        <span><i className="is-dropoff" /> Ziel</span>
        <strong className="pp-map-legend-group">Linien</strong>
        <span data-testid="legend-line-pickup"><LineSample kind="pickup" /> {LINE_STYLE.pickup.label}</span>
        <span data-testid="legend-line-dropoff"><LineSample kind="dropoff" /> {LINE_STYLE.dropoff.label}</span>
        <span data-testid="legend-line-planned"><LineSample kind="planned" /> {LINE_STYLE.planned.label}</span>
      </div>

      {/* Fahrer-Panel (schwebend, wie beim Marktführer): Filter oben, Liste darunter, Klapp-Pfeil unten. */}
      <div className="pp-map-panel" data-testid="live-map-panel">
        <div className="pp-map-panel-filters">
          <div className="pp-map-panel-row">
            <select value="driver" disabled className="pp-map-select pp-map-select-kind" aria-label="Ansicht">
              <option value="driver">Fahrer</option>
            </select>
            <div className="pp-map-search">
              <Search size={14} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name" aria-label="Fahrer suchen" />
            </div>
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="pp-map-select" aria-label="Status filtern">
            <option value="online">Status: Online</option>
            <option value="free">Frei</option>
            <option value="pickup">Zum Abholort</option>
            <option value="pax">Mit Fahrgast</option>
            <option value="offline">Nur Offline</option>
            <option value="all">Alle (auch Offline)</option>
          </select>
          {/* Zähler mit Phasenpunkt: dieselbe Farbe wie Auto und Linie auf der Karte. */}
          <div className="pp-map-counts" data-testid="live-map-counts" aria-label="Aktuelle Fahrerzustände">
            <span data-phase="pickup"><em><i style={{ background: PHASE_COLOR.pickup }} />Zum Abholort</em> <strong>{pickupCount}</strong></span>
            <span data-phase="pax"><em><i style={{ background: PHASE_COLOR.pax }} />Mit Fahrgast</em> <strong>{paxCount}</strong></span>
          </div>
        </div>

        {listOpen && (
          <div className="pp-map-list" data-testid="live-map-list">
            {filtered.length === 0 && <div className="pp-map-empty">Keine Fahrer</div>}
            {filtered.map(d => {
              const selected = selectedId === d.id;
              const ride = rideByDriver[d.id];
              return (
                <div key={d.id} className={`pp-map-driver ${selected ? 'is-selected' : ''}`} data-testid="live-map-driver" data-selected={selected ? '1' : '0'}>
                  <div className="pp-map-driver-head" role="button" tabIndex={0}
                    onClick={() => selectDriver(d)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectDriver(d); } }}>
                    <Avatar d={d} />
                    <div className="pp-map-driver-text">
                      <div className="pp-map-driver-name">{d.name}</div>
                      {d.phone && <div className="pp-map-driver-phone"><Phone size={11} /> {d.phone}</div>}
                    </div>
                    <div className="pp-map-driver-menu">
                      <button type="button" className={`pp-map-more ${menuFor === d.id ? 'is-open' : ''}`} aria-label={`Aktionen für ${d.name}`} aria-expanded={menuFor === d.id}
                        onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === d.id ? null : d.id); }}>
                        <MoreHorizontal size={18} />
                      </button>
                      {menuFor === d.id && (
                        <div className="pp-map-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                          <button type="button" role="menuitem" onClick={() => showOnMap(d)} disabled={!positions[d.id] || !d.is_online}>
                            <LocateFixed size={14} /> Auf Karte zeigen
                          </button>
                          {d.phone && <a role="menuitem" href={`tel:${String(d.phone).replace(/\s+/g, '')}`}><Phone size={14} /> Anrufen</a>}
                          <a role="menuitem" href={`/drivers/${d.id}`}><FileText size={14} /> Fahrer-Akte</a>
                        </div>
                      )}
                    </div>
                  </div>
                  {selected && (
                    <div className="pp-map-driver-detail" data-testid="live-map-driver-detail">
                      <div className="pp-map-detail-row">
                        <i className="pp-map-detail-dot" style={{ background: driverColor(d) }} />
                        <span style={{ color: driverTextColor(d), fontWeight: 700 }}>
                          {driverLabel(d)}{!!d.ride_followup_count && <> · danach {d.ride_followup_count} Folgefahrt{d.ride_followup_count === 1 ? '' : 'en'}</>}
                        </span>
                      </div>
                      <div className="pp-map-detail-row">
                        <Car size={14} />
                        <span>{vehicleLine(d) || 'Kein Fahrzeug hinterlegt'}</span>
                      </div>
                      {ride && d.is_online && (
                        <div className="pp-map-detail-route">
                          <span><i className="is-pickup" />{ride.pickup_address || 'Abholort nicht verfügbar'}</span>
                          <span><i className="is-dropoff" />{ride.dropoff_address || 'Ziel nicht verfügbar'}</span>
                        </div>
                      )}
                      {(followupsByDriver[d.id] || []).map(followup => (
                        <div className="pp-map-detail-route" data-testid="live-map-followup" key={followup.id}>
                          <strong>Danach Folgefahrt</strong>
                          <span><i className="is-pickup" />{followup.pickup_address || 'Abholort nicht verfügbar'}</span>
                          <span><i className="is-dropoff" />{followup.dropoff_address || 'Ziel nicht verfügbar'}</span>
                        </div>
                      ))}
                      {!positions[d.id] && d.is_online && <div className="pp-map-detail-hint">Noch keine Position empfangen.</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <button type="button" className="pp-map-collapse" onClick={() => setListOpen(o => !o)} aria-label={listOpen ? 'Liste einklappen' : 'Liste ausklappen'} aria-expanded={listOpen}>
          {listOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>
    </div>
  );
}
